import {
  cityLocalNow,
  isNightWithin,
  solarBounds,
  type SolarBounds,
} from "@/utils/solar";
import type { DailyWeatherForecastData } from "@/types/forecast";

export const ANCHOR_RADIUS_MINUTES = 50;

type Anchor = { lat: number; lon: number };
const ZONE_ANCHORS: Record<string, Anchor> = {
  "Europe/Kyiv": { lat: 50.4501, lon: 30.5234 },
  "Europe/Kiev": { lat: 50.4501, lon: 30.5234 },
};

export type DayNightInput = {
  lat: number;
  lon: number;
  timezone?: string;
  utcOffsetSeconds?: number;
  nowMs?: number;
};

const boundsDelta = (a: SolarBounds, b: SolarBounds): number | null => {
  if (a.kind !== "normal" || b.kind !== "normal") {
    return a.kind === b.kind ? 0 : null;
  }
  return Math.max(
    Math.abs(a.sunriseMin - b.sunriseMin),
    Math.abs(a.sunsetMin - b.sunsetMin),
  );
};

export const resolveIsNight = ({
  lat,
  lon,
  timezone,
  utcOffsetSeconds,
  nowMs,
}: DayNightInput): boolean => {
  const at = nowMs ?? Date.now();
  if (
    typeof utcOffsetSeconds !== "number" ||
    !Number.isFinite(utcOffsetSeconds) ||
    !Number.isFinite(lat) ||
    !Number.isFinite(lon)
  ) {
    return false;
  }

  const { minuteOfDay, dayKey } = cityLocalNow(utcOffsetSeconds, at);
  const own = solarBounds(lat, lon, dayKey, utcOffsetSeconds);

  const anchor = timezone ? ZONE_ANCHORS[timezone] : undefined;
  if (!anchor) return isNightWithin(own, minuteOfDay);

  const anchorBounds = solarBounds(
    anchor.lat,
    anchor.lon,
    dayKey,
    utcOffsetSeconds,
  );
  const delta = boundsDelta(own, anchorBounds);

  if (delta === null || delta > ANCHOR_RADIUS_MINUTES) {
    return isNightWithin(own, minuteOfDay);
  }

  return isNightWithin(anchorBounds, minuteOfDay);
};

export const isNightForDay = (
  day?: DailyWeatherForecastData,
  nowMs?: number,
): boolean => {
  if (!day) return false;
  return resolveIsNight({
    lat: day.lat as number,
    lon: day.lon as number,
    timezone: day.timezone,
    utcOffsetSeconds: day.utcOffsetSeconds,
    nowMs,
  });
};

export const resolveIsNightAtHour = (
  input: Omit<DayNightInput, "nowMs"> & { dayKey: string; hour: number },
): boolean => {
  const { lat, lon, timezone, utcOffsetSeconds, dayKey, hour } = input;
  if (
    typeof utcOffsetSeconds !== "number" ||
    !Number.isFinite(utcOffsetSeconds) ||
    !Number.isFinite(lat) ||
    !Number.isFinite(lon)
  ) {
    return false;
  }

  const own = solarBounds(lat, lon, dayKey, utcOffsetSeconds);
  const anchor = timezone ? ZONE_ANCHORS[timezone] : undefined;
  const minuteOfDay = hour * 60 + 30;

  if (!anchor) return isNightWithin(own, minuteOfDay);

  const anchorBounds = solarBounds(
    anchor.lat,
    anchor.lon,
    dayKey,
    utcOffsetSeconds,
  );
  const delta = boundsDelta(own, anchorBounds);
  if (delta === null || delta > ANCHOR_RADIUS_MINUTES) {
    return isNightWithin(own, minuteOfDay);
  }
  return isNightWithin(anchorBounds, minuteOfDay);
};

export const isNightForDayAtHour = (
  day: DailyWeatherForecastData | undefined,
  hour: number,
): boolean => {
  if (!day) return false;
  return resolveIsNightAtHour({
    lat: day.lat as number,
    lon: day.lon as number,
    timezone: day.timezone,
    utcOffsetSeconds: day.utcOffsetSeconds,
    dayKey: day.date,
    hour,
  });
};
